/**
 * TikTok Ads API Helpers (Story IMC-8, Task 1)
 *
 * Calls the TikTok Marketing API v1.3 for ads reporting.
 * Separate from the organic Business API (different base URL, auth header, endpoints).
 *
 * CRITICAL differences from organic API:
 * - Base URL: business-api.tiktok.com (NOT open.tiktokapis.com)
 * - Auth header: Access-Token (NOT Authorization: Bearer)
 * - Report endpoint: POST /report/integrated/get/
 * - Rate limit: 600 req/min (same as organic, separate quota)
 * - 6-second focused views (video_watched_6s) is TikTok's primary video metric
 *
 * ADR-013: Adapter Pattern for API Isolation
 */

import type { MetricDefinition, DimensionDefinition, AdapterResult } from "../adapter";
import { adapterErr, adapterOk } from "../adapter";
import { safeFetch, sanitizeErrorMessage } from "../safe-fetch";
import { parseTikTokApiError } from "./errors";

// ============================================================================
// Constants
// ============================================================================

/** TikTok Marketing API v1.3 base URL */
const TIKTOK_ADS_API_BASE = "https://business-api.tiktok.com/open_api/v1.3";

/** Maximum rows per report request */
const REPORT_PAGE_SIZE = 1000;

/** Safety limit on report pages to prevent unbounded API calls from malformed responses */
const MAX_REPORT_PAGES = 100;

// ============================================================================
// Types
// ============================================================================

interface TikTokAdsReportRequest {
  advertiser_id: string;
  report_type: "BASIC" | "AUDIENCE";
  data_level: "AUCTION_ADVERTISER" | "AUCTION_CAMPAIGN" | "AUCTION_ADGROUP" | "AUCTION_AD";
  dimensions: string[];
  metrics: string[];
  start_date: string;
  end_date: string;
  page: number;
  page_size: number;
}

interface TikTokAdsReportRow {
  dimensions: Record<string, string>;
  metrics: Record<string, string>;
}

interface TikTokAdsReportResponse {
  code: number;
  message: string;
  request_id: string;
  data: {
    list: TikTokAdsReportRow[];
    page_info: {
      total_number: number;
      page: number;
      page_size: number;
      total_page: number;
    };
  };
}

// ============================================================================
// Auth Header Builder
// ============================================================================

/**
 * Builds TikTok Marketing API headers.
 * Uses Access-Token header (NOT Authorization: Bearer).
 */
export function buildTikTokAdsHeaders(accessToken: string): Record<string, string> {
  return {
    "Access-Token": accessToken,
    "Content-Type": "application/json",
  };
}

// ============================================================================
// Report Fetcher
// ============================================================================

/**
 * Fetches a TikTok Ads report via POST /report/integrated/get/.
 * Handles pagination automatically.
 */
export async function fetchTikTokAdsReport(
  accessToken: string,
  advertiserId: string,
  dateRange: { start: Date; end: Date },
  reportType: "BASIC" | "AUDIENCE",
  dataLevel: TikTokAdsReportRequest["data_level"],
  metrics: string[],
  dimensions: string[]
): Promise<AdapterResult<TikTokAdsReportRow[]>> {
  const startDate = dateRange.start.toISOString().split("T")[0]!;
  const endDate = dateRange.end.toISOString().split("T")[0]!;

  const allRows: TikTokAdsReportRow[] = [];
  let page = 1;
  let totalPages = 1;

  do {
    const requestBody: TikTokAdsReportRequest = {
      advertiser_id: advertiserId,
      report_type: reportType,
      data_level: dataLevel,
      dimensions,
      metrics,
      start_date: startDate,
      end_date: endDate,
      page,
      page_size: REPORT_PAGE_SIZE,
    };

    const result = await safeFetch(
      `${TIKTOK_ADS_API_BASE}/report/integrated/get/`,
      {
        method: "POST",
        headers: buildTikTokAdsHeaders(accessToken),
        body: JSON.stringify(requestBody),
      },
      25_000
    );

    if (!result.success) {
      return adapterErr(result.error as Parameters<typeof adapterErr>[0]);
    }

    let responseBody: TikTokAdsReportResponse;
    try {
      responseBody = (await result.data.json()) as TikTokAdsReportResponse;
    } catch (err) {
      return adapterErr({
        code: "invalid_response",
        message: sanitizeErrorMessage(
          `Failed to parse TikTok Ads report response: ${err instanceof Error ? err.message : String(err)}`
        ),
        retryable: false,
      });
    }

    // TikTok Marketing API uses numeric code 0 for success
    if (responseBody.code !== 0) {
      const apiError = parseTikTokApiError(responseBody);
      if (apiError) {
        const codeMap: Record<string, Parameters<typeof adapterErr>[0]["code"]> = {
          token_expired: "token_expired",
          permission_denied: "permission_denied",
          rate_limited: "rate_limited",
          provider_unavailable: "provider_unavailable",
        };
        return adapterErr({
          code: codeMap[apiError.type] ?? "invalid_response",
          message: sanitizeErrorMessage(`TikTok Ads report: ${apiError.message}`),
          retryable: apiError.retryable,
        });
      }
      return adapterErr({
        code: "invalid_response",
        message: sanitizeErrorMessage(`TikTok Ads report error (code ${responseBody.code}): ${responseBody.message}`),
        retryable: false,
      });
    }

    const rows = responseBody.data?.list ?? [];
    allRows.push(...rows);

    totalPages = responseBody.data?.page_info?.total_page ?? 1;
    page++;
  } while (page <= totalPages && page <= MAX_REPORT_PAGES);

  return adapterOk(allRows);
}

// ============================================================================
// Advertiser Catalog Discovery (Story 31.22)
// ============================================================================

/** Max advertiser ids per /advertiser/info/ call (Marketing API batch limit) */
const ADVERTISER_INFO_CHUNK_SIZE = 100;

/**
 * Defence cap on enrichment chunks (50 × 100 = 5,000 advertisers).
 * Hitting it emits a `tiktok_advertiser_discovery_partial` warn (AC #2).
 */
const MAX_ADVERTISER_INFO_CHUNKS = 50;

/** Timeout per discovery HTTP call (story Task 1.2) */
const DISCOVERY_TIMEOUT_MS = 10_000;

/** One advertiser row from GET /oauth2/advertiser/get/ */
export interface TikTokAdvertiserSummary {
  /** Bare numeric string — FROZEN format, equals the `advertiser_id` axis of fetchTikTokAdsReport */
  advertiserId: string;
  /** May be "" — callers apply the name fallback chain (31-4 non-null-name contract) */
  advertiserName: string;
}

/** Enrichment fields from GET /advertiser/info/ — absent keys are omitted, never undefined */
export interface TikTokAdvertiserInfo {
  name?: string;
  currency?: string;
  timezone?: string;
  displayTimezone?: string;
  status?: string;
}

/** Correlation ids for structured warn logs (31-24 cron observability) */
export interface TikTokDiscoveryLogContext {
  tenantId?: string;
  integrationId?: string;
}

/** Marketing API success/error envelope — numeric `code === 0` is success */
interface TikTokAdsEnvelope<T> {
  code?: number;
  message?: string;
  request_id?: string;
  data?: T;
}

/**
 * Removes the app credential VALUES from a message before it can reach an
 * AdapterError or warn payload (Story 31.22, AC #8). TikTok's
 * /oauth2/advertiser/get/ carries `app_id`/`secret` in the QUERY STRING, and
 * the generic `sanitizeErrorMessage` does not know the literal values — so
 * discovery error construction never interpolates the request URL AND
 * defensively strips the literal credential strings in case the provider
 * echoes them in a response body.
 */
function redactAppCredentials(message: string, appId: string, secret: string): string {
  let redacted = message;
  // Strip BOTH the raw value and its URL-encoded form: the request URL
  // carries encodeURIComponent(secret), so a provider/proxy echoing the URL
  // leaks the ENCODED variant, which a raw-only split would miss.
  for (const value of [secret, encodeURIComponent(secret), appId, encodeURIComponent(appId)]) {
    if (value) redacted = redacted.split(value).join("[REDACTED]");
  }
  return redacted;
}

/**
 * Maps a non-zero Marketing API numeric code to an adapter error code
 * (Story 31.22 — same codeMap SHAPE as the fetchTikTokAdsReport mapping).
 *
 * IMPORTANT — auth/token-class business-api codes map to `permission_denied`
 * here, NOT `token_expired`: `discoverAccounts` calls the organic
 * `user/info` endpoint FIRST, so by the time the advertiser list is fetched
 * the token is known-valid for the grant. A business-api auth failure
 * therefore means the ADS SURFACE is unauthorized for this token
 * (Login-Kit-only token / app not approved for the Marketing API — story
 * pre-mortem A), which is exactly the AC #6(c) graceful organic-only
 * degrade class. Everything else stays loud (AC #7).
 */
function mapTikTokAdsDiscoveryError(
  code: number,
  message: string
): { code: Parameters<typeof adapterErr>[0]["code"]; retryable: boolean } {
  // Rate limiting — retryable, the 31-24 cron's backoff owns it (AC #7)
  if (code === 40100) return { code: "rate_limited", retryable: true };

  // Auth/token/permission class → ads-surface-unauthorized degrade (AC #6(c)):
  // 40101 auth failed, 40102/40104 token empty, 40105 token invalid,
  // 40106 token format, 40107 token expired (business-api numbering).
  if (code >= 40101 && code <= 40199) return { code: "permission_denied", retryable: false };

  // Message-keyword fallback for undocumented permission codes
  if (/permission|not authorized|unauthoriz|no access/i.test(message)) {
    return { code: "permission_denied", retryable: false };
  }

  // Server-side failure → retryable provider_unavailable
  if (code >= 50000) return { code: "provider_unavailable", retryable: true };

  // Anything else (param errors, unknown codes) — per-cause loud failure
  return { code: "invalid_response", retryable: false };
}

/**
 * Fetches the FULL advertiser catalog via GET /oauth2/advertiser/get/
 * (Story 31.22, AC #1).
 *
 * - `app_id` + `secret` ride the query string (TikTok's design); the
 *   `Access-Token` header carries the same hybrid token used by the organic
 *   API (`buildTikTokAdsHeaders` — NOT `Authorization: Bearer`).
 * - The endpoint returns the full grant list in one response (no cursor —
 *   validated against the official SDK + Postman collection).
 * - A missing/non-array `data.list` on a `code === 0` body is treated as `[]`.
 * - Error messages NEVER include the request URL and are stripped of the
 *   literal app credentials (AC #8).
 */
export async function fetchTikTokAdvertiserList(
  accessToken: string,
  appId: string,
  secret: string
): Promise<AdapterResult<TikTokAdvertiserSummary[]>> {
  const url =
    `${TIKTOK_ADS_API_BASE}/oauth2/advertiser/get/` +
    `?app_id=${encodeURIComponent(appId)}&secret=${encodeURIComponent(secret)}`;

  const result = await safeFetch(
    url,
    { method: "GET", headers: buildTikTokAdsHeaders(accessToken) },
    DISCOVERY_TIMEOUT_MS
  );

  if (!result.success) {
    // Per-cause passthrough of the safeFetch envelope (provider_unavailable /
    // rate_limited / network_error keep their codes + retryable flags), but
    // REBUILD the message defensively: never the URL, never the credentials.
    //
    // EXCEPTION — safeFetch coerces HTTP 401 → `token_expired`, but
    // discoverAccounts calls the organic user/info endpoint FIRST, so the
    // token is known-valid by the time this runs. An HTTP-401 from the
    // business-api gateway therefore means the ADS SURFACE is unauthorized
    // for this token (Login-Kit-only token / unapproved app — same rationale
    // as the 401xx body-code mapping in mapTikTokAdsDiscoveryError), i.e.
    // the AC #6(c) organic-only degrade class — NOT a refresh trigger.
    const code =
      result.error.code === "token_expired"
        ? ("permission_denied" as const)
        : (result.error.code as Parameters<typeof adapterErr>[0]["code"]);
    return adapterErr({
      code,
      message: redactAppCredentials(
        sanitizeErrorMessage(`TikTok advertiser list: ${result.error.message}`),
        appId,
        secret
      ),
      retryable: result.error.retryable,
      // Preserve backoff/diagnostic metadata for the 31-24 cron (Retry-After
      // parsed by safeFetch on 429s); providerMessage gets the same credential
      // redaction as the message — sanitizeErrorMessage alone does not know
      // TikTok's `secret=` param name.
      ...(result.error.retryAfterMs !== undefined ? { retryAfterMs: result.error.retryAfterMs } : {}),
      ...(result.error.providerStatus !== undefined ? { providerStatus: result.error.providerStatus } : {}),
      ...(result.error.providerMessage !== undefined
        ? { providerMessage: redactAppCredentials(result.error.providerMessage, appId, secret) }
        : {}),
    });
  }

  let responseBody: TikTokAdsEnvelope<{ list?: unknown }>;
  try {
    responseBody = (await result.data.json()) as TikTokAdsEnvelope<{ list?: unknown }>;
  } catch (err) {
    return adapterErr({
      code: "invalid_response",
      message: redactAppCredentials(
        sanitizeErrorMessage(
          `Failed to parse TikTok advertiser list response: ${err instanceof Error ? err.message : String(err)}`
        ),
        appId,
        secret
      ),
      retryable: false,
    });
  }

  if (responseBody.code !== 0) {
    const mapped = mapTikTokAdsDiscoveryError(responseBody.code ?? -1, responseBody.message ?? "");
    return adapterErr({
      code: mapped.code,
      message: redactAppCredentials(
        sanitizeErrorMessage(
          `TikTok advertiser list error (code ${responseBody.code ?? "unknown"}): ${responseBody.message ?? "no message"}`
        ),
        appId,
        secret
      ),
      retryable: mapped.retryable,
    });
  }

  const rawList = Array.isArray(responseBody.data?.list) ? responseBody.data.list : [];
  const advertisers: TikTokAdvertiserSummary[] = [];
  for (const item of rawList as Array<{ advertiser_id?: string | number; advertiser_name?: string }>) {
    if (item?.advertiser_id === undefined || item.advertiser_id === null || item.advertiser_id === "") {
      continue; // membership requires an id — skip malformed rows
    }
    advertisers.push({
      advertiserId: String(item.advertiser_id),
      advertiserName: typeof item.advertiser_name === "string" ? item.advertiser_name : "",
    });
  }
  return adapterOk(advertisers);
}

/**
 * Best-effort currency/timezone enrichment via GET /advertiser/info/
 * (Story 31.22, AC #2). Chunks ids at ≤100 per call with a 50-chunk defence
 * cap.
 *
 * CONTRACT: this helper NEVER returns an error result — enrichment failure
 * degrades metadata, never row membership. A failed chunk (network error,
 * non-zero code, malformed JSON) emits a structured
 * `tiktok_advertiser_enrichment_failed` warn (chunk size + error code +
 * caller correlation ids) and its ids are simply absent from the returned
 * map; the caller keeps those advertisers without enrichment keys.
 * `AdapterResult` is kept for module signature consistency.
 */
export async function fetchTikTokAdvertiserInfo(
  accessToken: string,
  advertiserIds: string[],
  logContext?: TikTokDiscoveryLogContext
): Promise<AdapterResult<Map<string, TikTokAdvertiserInfo>>> {
  const infoMap = new Map<string, TikTokAdvertiserInfo>();

  const chunks: string[][] = [];
  for (let i = 0; i < advertiserIds.length; i += ADVERTISER_INFO_CHUNK_SIZE) {
    chunks.push(advertiserIds.slice(i, i + ADVERTISER_INFO_CHUNK_SIZE));
  }

  if (chunks.length > MAX_ADVERTISER_INFO_CHUNKS) {
    console.warn(
      JSON.stringify({
        event: "tiktok_advertiser_discovery_partial",
        tenantId: logContext?.tenantId,
        integrationId: logContext?.integrationId,
        totalAdvertisers: advertiserIds.length,
        enrichedCap: MAX_ADVERTISER_INFO_CHUNKS * ADVERTISER_INFO_CHUNK_SIZE,
        message:
          "TikTok advertiser enrichment hit the max-chunk defence cap; advertisers beyond the cap stay in the catalog without currency/timezone metadata",
      })
    );
    chunks.length = MAX_ADVERTISER_INFO_CHUNKS;
  }

  const fields = JSON.stringify([
    "advertiser_id",
    "name",
    "currency",
    "timezone",
    "display_timezone",
    "status",
  ]);

  for (const chunk of chunks) {
    const url =
      `${TIKTOK_ADS_API_BASE}/advertiser/info/` +
      `?advertiser_ids=${encodeURIComponent(JSON.stringify(chunk))}` +
      `&fields=${encodeURIComponent(fields)}`;

    const warnChunkFailure = (errorCode: string, providerCode?: number) => {
      console.warn(
        JSON.stringify({
          event: "tiktok_advertiser_enrichment_failed",
          tenantId: logContext?.tenantId,
          integrationId: logContext?.integrationId,
          chunkSize: chunk.length,
          errorCode,
          ...(providerCode !== undefined ? { providerCode } : {}),
          message:
            "TikTok advertiser info enrichment failed for a chunk; affected advertisers remain in the catalog without enrichment metadata",
        })
      );
    };

    const result = await safeFetch(
      url,
      { method: "GET", headers: buildTikTokAdsHeaders(accessToken) },
      DISCOVERY_TIMEOUT_MS
    );

    if (!result.success) {
      warnChunkFailure(result.error.code);
      continue;
    }

    let responseBody: TikTokAdsEnvelope<{ list?: unknown }>;
    try {
      responseBody = (await result.data.json()) as TikTokAdsEnvelope<{ list?: unknown }>;
    } catch {
      warnChunkFailure("invalid_response");
      continue;
    }

    if (responseBody.code !== 0) {
      const mapped = mapTikTokAdsDiscoveryError(responseBody.code ?? -1, responseBody.message ?? "");
      warnChunkFailure(mapped.code, responseBody.code);
      continue;
    }

    const rawList = Array.isArray(responseBody.data?.list) ? responseBody.data.list : [];
    for (const item of rawList as Array<{
      advertiser_id?: string | number;
      name?: string;
      currency?: string;
      timezone?: string;
      display_timezone?: string;
      status?: string;
    }>) {
      if (item?.advertiser_id === undefined || item.advertiser_id === null || item.advertiser_id === "") {
        continue;
      }
      // Omit absent keys entirely — this object flows into
      // external_account_metadata JSON (no `undefined` values).
      const info: TikTokAdvertiserInfo = {};
      if (typeof item.name === "string" && item.name) info.name = item.name;
      if (typeof item.currency === "string" && item.currency) info.currency = item.currency;
      if (typeof item.timezone === "string" && item.timezone) info.timezone = item.timezone;
      if (typeof item.display_timezone === "string" && item.display_timezone) {
        info.displayTimezone = item.display_timezone;
      }
      if (typeof item.status === "string" && item.status) info.status = item.status;
      infoMap.set(String(item.advertiser_id), info);
    }
  }

  return adapterOk(infoMap);
}

// ============================================================================
// Ads Metric Definitions (46 metrics)
// ============================================================================

export const TIKTOK_ADS_METRICS: MetricDefinition[] = [
  // ── Delivery & Cost (9) ──
  { key: "spend", label: "Ad Spend", type: "currency", category: "Delivery", dataType: "Currency", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart", "bar-chart", "data-table"] },
  { key: "impressions", label: "Impressions", type: "number", category: "Delivery", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart", "bar-chart"] },
  { key: "clicks", label: "Clicks", type: "number", category: "Delivery", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart", "bar-chart"] },
  { key: "ctr", label: "CTR", type: "percentage", category: "Delivery", dataType: "Percentage", aggregationKind: "ratio", ratioComponents: { numerator: "clicks", denominator: "impressions" }, compatibleWidgets: ["kpi", "gauge"] },
  { key: "cpc", label: "CPC", type: "currency", category: "Delivery", dataType: "Currency", aggregationKind: "ratio", ratioComponents: { numerator: "spend", denominator: "clicks" }, compatibleWidgets: ["kpi"] },
  { key: "cpm", label: "CPM", type: "currency", category: "Delivery", dataType: "Currency", aggregationKind: "ratio", ratioComponents: { numerator: "spend", denominator: "impressions" }, compatibleWidgets: ["kpi"] },
  { key: "reach", label: "Reach", type: "number", category: "Delivery", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart"] },
  { key: "frequency", label: "Frequency", type: "number", category: "Delivery", dataType: "Decimal", aggregationKind: "ratio", ratioComponents: { numerator: "impressions", denominator: "reach" }, compatibleWidgets: ["kpi"] },
  { key: "cost_per_1000_reached", label: "Cost per 1K Reached", type: "currency", category: "Delivery", dataType: "Currency", aggregationKind: "ratio", ratioComponents: { numerator: "spend", denominator: "reach" }, compatibleWidgets: ["kpi"] },

  // ── Video Play (9) ──
  { key: "video_play_actions", label: "Video Plays", type: "number", category: "Video", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart"] },
  { key: "video_watched_2s", label: "2s Video Views", type: "number", category: "Video", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi"] },
  { key: "video_watched_6s", label: "6s Focused Views", type: "number", category: "Video", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart"] },
  { key: "average_video_play", label: "Avg Play Time", type: "duration", category: "Video", dataType: "Duration", aggregationKind: "ratio", ratioComponents: { denominator: "video_play_actions" }, compatibleWidgets: ["kpi"] },
  { key: "average_video_play_per_user", label: "Avg Play Time/User", type: "duration", category: "Video", dataType: "Duration", aggregationKind: "ratio", ratioComponents: { denominator: "reach" }, compatibleWidgets: ["kpi"] },
  { key: "video_views_p25", label: "Video 25%", type: "number", category: "Video", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "funnel"] },
  { key: "video_views_p50", label: "Video 50%", type: "number", category: "Video", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "funnel"] },
  { key: "video_views_p75", label: "Video 75%", type: "number", category: "Video", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "funnel"] },
  { key: "video_views_p100", label: "Video 100%", type: "number", category: "Video", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "funnel"] },

  // ── Engagement (8) ──
  { key: "engagements", label: "Total Engagements (Ad)", type: "number", category: "Engagement", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart"] },
  { key: "likes", label: "Likes (Ad)", type: "number", category: "Engagement", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi"] },
  { key: "comments", label: "Comments (Ad)", type: "number", category: "Engagement", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi"] },
  { key: "shares", label: "Shares (Ad)", type: "number", category: "Engagement", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi"] },
  { key: "follows", label: "Follows (Ad)", type: "number", category: "Engagement", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi"] },
  { key: "profile_visits", label: "Profile Visits (Ad)", type: "number", category: "Engagement", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi"] },
  { key: "profile_visits_rate", label: "Profile Visit Rate", type: "percentage", category: "Engagement", dataType: "Percentage", aggregationKind: "ratio", ratioComponents: { numerator: "profile_visits", denominator: "impressions" }, compatibleWidgets: ["kpi"] },
  { key: "engagement_rate", label: "Engagement Rate (Ad)", type: "percentage", category: "Engagement", dataType: "Percentage", aggregationKind: "ratio", ratioComponents: { denominator: "impressions" }, compatibleWidgets: ["kpi", "gauge"] },

  // ── Conversions (11) ──
  { key: "conversions", label: "Conversions", type: "number", category: "Conversion", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart"] },
  { key: "conversion_rate", label: "Conversion Rate", type: "percentage", category: "Conversion", dataType: "Percentage", aggregationKind: "ratio", ratioComponents: { numerator: "conversions", denominator: "clicks" }, compatibleWidgets: ["kpi", "gauge"] },
  { key: "cost_per_conversion", label: "Cost per Conversion", type: "currency", category: "Conversion", dataType: "Currency", aggregationKind: "ratio", ratioComponents: { numerator: "spend", denominator: "conversions" }, compatibleWidgets: ["kpi"] },
  { key: "total_purchase_value", label: "Purchase Revenue", type: "currency", category: "Conversion", dataType: "Currency", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart"] },
  { key: "purchase", label: "Purchases", type: "number", category: "Conversion", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi"] },
  { key: "add_to_cart", label: "Add to Cart", type: "number", category: "Conversion", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi"] },
  { key: "initiate_checkout", label: "Initiate Checkout", type: "number", category: "Conversion", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi"] },
  { key: "complete_registration", label: "Complete Registration", type: "number", category: "Conversion", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi"] },
  { key: "app_install", label: "App Installs", type: "number", category: "Conversion", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi"] },
  { key: "total_sales_lead", label: "Leads", type: "number", category: "Conversion", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi"] },
  { key: "view_content", label: "View Content", type: "number", category: "Conversion", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi"] },

  // ── ROAS (2) + Conversion (IMC-26: 1) ──
  { key: "purchase_roas", label: "Purchase ROAS", type: "number", category: "ROAS", dataType: "Decimal", aggregationKind: "ratio", ratioComponents: { denominator: "spend" }, compatibleWidgets: ["kpi", "gauge"] },
  { key: "total_onsite_shopping_roas", label: "Onsite Shopping ROAS", type: "number", category: "ROAS", dataType: "Decimal", aggregationKind: "ratio", ratioComponents: { denominator: "spend" }, compatibleWidgets: ["kpi"] },
  // IMC-26: Real-time purchase conversion — TikTok API field `real_time_result` when
  // the campaign's optimization goal is Purchase. Only available for TikTok Shop campaigns.
  { key: "real_time_purchase", label: "Real-Time Purchases", type: "number", category: "Conversion", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi"] },

  // ── TikTok Shop (6) ──
  { key: "product_page_views", label: "Product Page Views", type: "number", category: "Shop", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi"] },
  { key: "add_to_cart_shop", label: "Shop Add to Cart", type: "number", category: "Shop", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi"] },
  { key: "initiate_checkout_shop", label: "Shop Checkout", type: "number", category: "Shop", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi"] },
  { key: "complete_payment_shop", label: "Shop Payments", type: "number", category: "Shop", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi"] },
  { key: "gross_revenue", label: "Shop Gross Revenue", type: "currency", category: "Shop", dataType: "Currency", aggregationKind: "additive", compatibleWidgets: ["kpi"] },
  { key: "gmv", label: "GMV", type: "currency", category: "Shop", dataType: "Currency", aggregationKind: "additive", compatibleWidgets: ["kpi"] },
];

// ============================================================================
// Ads Dimension Definitions (20 dimensions)
// ============================================================================

export const TIKTOK_ADS_DIMENSIONS: DimensionDefinition[] = [
  // ── Campaign Structure (6) ──
  { key: "campaign_id", label: "Campaign ID", type: "Structure", exampleValues: ["1234567890"] },
  { key: "campaign_name", label: "Campaign Name", type: "Structure", exampleValues: ["Spring Sale 2026"] },
  { key: "adgroup_id", label: "Ad Group ID", type: "Structure", exampleValues: ["9876543210"] },
  { key: "adgroup_name", label: "Ad Group Name", type: "Structure", exampleValues: ["18-34 Female"] },
  { key: "ad_id", label: "Ad ID", type: "Structure", exampleValues: ["5555555555"] },
  { key: "ad_name", label: "Ad Name", type: "Structure", exampleValues: ["Video Ad - Product Launch"] },

  // ── Demographics (2) ──
  { key: "age", label: "Age", type: "Demo", exampleValues: ["AGE_13_17", "AGE_18_24", "AGE_25_34", "AGE_35_44", "AGE_45_54", "AGE_55_100"] },
  { key: "gender", label: "Gender", type: "Demo", exampleValues: ["MALE", "FEMALE", "UNKNOWN"] },

  // ── Geography (1) ──
  { key: "country_code", label: "Country", type: "Geo", exampleValues: ["US", "GB", "DE"] },

  // ── Device (1) ──
  { key: "platform", label: "Platform", type: "Tech", exampleValues: ["IOS", "ANDROID"] },

  // ── Placement (1) ──
  { key: "placement", label: "Placement", type: "Delivery", exampleValues: ["TIKTOK", "PANGLE", "GLOBAL_APP_BUNDLE"] },

  // ── Time (1) ──
  { key: "stat_time_day", label: "Date", type: "Time", exampleValues: ["2026-03-01"] },
  { key: "stat_time_hour", label: "Hour", type: "Time", exampleValues: ["2026-03-01 14:00:00"] },

  // ── Ads-specific (2) ──
  { key: "campaign_type", label: "Campaign Type", type: "Structure", exampleValues: ["REGULAR", "SMART_PERFORMANCE"] },
  { key: "ad_format", label: "Ad Format", type: "Content", exampleValues: ["SINGLE_VIDEO", "SPARK_ADS", "CAROUSEL"] },

  // ── IMC-26: Audience targeting dimensions (5) — require report_type: "AUDIENCE" ──
  { key: "interest_category", label: "Interest Category", type: "Demo", exampleValues: ["Apparel & Accessories", "Beauty & Personal Care", "Food & Beverage"] },
  { key: "province_id", label: "Province/Region", type: "Geo", exampleValues: ["6254926", "4736286", "5128638"] },
  { key: "dma_id", label: "DMA (US)", type: "Geo", exampleValues: ["501", "803", "602"] },
  { key: "language", label: "Language", type: "Demo", exampleValues: ["en", "es", "zh", "fr"] },
  { key: "device_brand_id", label: "Device Brand", type: "Tech", exampleValues: ["Apple", "Samsung", "Huawei", "Xiaomi"] },
];

// ============================================================================
// Ads Report Metrics Lists (for API requests)
// ============================================================================

/** Core delivery metrics to request from TikTok Ads API */
export const ADS_DELIVERY_METRICS = [
  "spend", "impressions", "clicks", "ctr", "cpc", "cpm",
  "reach", "frequency", "cost_per_1000_reached",
];

/** Video play metrics to request from TikTok Ads API */
export const ADS_VIDEO_METRICS = [
  "video_play_actions", "video_watched_2s", "video_watched_6s",
  "average_video_play", "average_video_play_per_user",
  "video_views_p25", "video_views_p50", "video_views_p75", "video_views_p100",
];

/** Engagement metrics to request from TikTok Ads API */
export const ADS_ENGAGEMENT_METRICS = [
  "engagements", "likes", "comments", "shares", "follows",
  "profile_visits", "profile_visits_rate", "engagement_rate",
];

/** Conversion metrics to request from TikTok Ads API */
export const ADS_CONVERSION_METRICS = [
  "conversions", "conversion_rate", "cost_per_conversion",
  "total_purchase_value", "purchase", "add_to_cart",
  "initiate_checkout", "complete_registration", "app_install",
  "total_sales_lead", "view_content",
];

/**
 * ROAS metrics to request from TikTok Ads API.
 * NOTE: These are API field names (sent to TikTok), not internal metric keys.
 * `real_time_result` is remapped to `real_time_purchase` via ADS_FIELD_REMAP in flattenAdsReportRow.
 */
export const ADS_ROAS_METRICS = [
  "purchase_roas", "total_onsite_shopping_roas",
  "real_time_result", // IMC-26: remapped to real_time_purchase — only nonzero for Shop campaigns
];

/** TikTok Shop metrics to request from TikTok Ads API */
export const ADS_SHOP_METRICS = [
  "product_page_views", "add_to_cart_shop", "initiate_checkout_shop",
  "complete_payment_shop", "gross_revenue", "gmv",
];

/** All ads metrics combined for a single report request */
export const ALL_ADS_METRICS = [
  ...ADS_DELIVERY_METRICS,
  ...ADS_VIDEO_METRICS,
  ...ADS_ENGAGEMENT_METRICS,
  ...ADS_CONVERSION_METRICS,
  ...ADS_ROAS_METRICS,
  ...ADS_SHOP_METRICS,
];

/**
 * Parse a TikTok Ads report row's metric value to a number.
 * TikTok returns all metric values as strings.
 */
export function parseAdsMetricValue(value: string | undefined): number {
  if (value === undefined || value === "" || value === "-") return 0;
  const num = parseFloat(value);
  return Number.isFinite(num) ? num : 0;
}

/**
 * API field → internal field name mappings.
 * TikTok API returns `real_time_result` (generic real-time conversion count);
 * we store it as `real_time_purchase` since purchase is the primary use case.
 */
const ADS_FIELD_REMAP: Record<string, string> = {
  real_time_result: "real_time_purchase",
};

/**
 * Converts a TikTok Ads report row into a flat record with numeric values.
 * Merges dimensions and parsed metric values.
 * Applies field remapping (e.g., real_time_result → real_time_purchase).
 */
export function flattenAdsReportRow(row: TikTokAdsReportRow): Record<string, unknown> {
  const record: Record<string, unknown> = {};

  // Copy dimension values directly
  for (const [key, value] of Object.entries(row.dimensions)) {
    record[key] = value;
  }

  // Parse metric values to numbers, applying field remapping
  for (const [key, value] of Object.entries(row.metrics)) {
    const mappedKey = ADS_FIELD_REMAP[key] ?? key;
    record[mappedKey] = parseAdsMetricValue(value);
  }

  return record;
}
